import { React, useState, useEffect, useMemo } from "react"; // Added useMemo
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, TextField } from "@mui/material"; // Added TextField for search
import WarningIcon from '@mui/icons-material/Warning';
import { red } from '@mui/material/colors';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from "sweetalert2";
import 'react-datepicker/dist/react-datepicker.css';
import ServiceSpecializationAddModel from "./ServiceSpecializationAddModel";
import ServiceSpecializationEditModel from "./ServiceSpecializationEditModel";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { useNavigate } from "react-router-dom";
import LogoutButton from "layouts/logout";


export default function ServiceSpecialization() {

    useEffect(() => {
        disableBackNavigation();
        fetchServiceSpecializations();
    }, []);


    // ----------------------------------------------------------
    // Model State Variables And Handles
    // ----------------------------------------------------------

    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const navigate = useNavigate();

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
        fetchServiceSpecializations();
    };

    const handleClose = () => {
        setEditInput({});
        setOpenAddModal(false);
        setOpenEditModal(false);
        setOpenDeleteModal(false);
        fetchServiceSpecializations();
    };

    // ----------------------------------------------------------
    // Service Specialization State Variables And Handles
    // ----------------------------------------------------------

    const [editInput, setEditInput] = useState({});
    const [loading, setLoading] = useState(true);
    const [serviceSpecializations, setServiceSpecializations] = useState([]);
    const [deleteServiceSpecializationId, setDeleteServiceSpecializationId] = useState(null);

    // Pagination and Search states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredServiceSpecializations, setFilteredServiceSpecializations] = useState([]);


    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditInput(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // ----------------------------------------------------------
    // Data Table Properties
    // ----------------------------------------------------------

    // Effect for filtering based on search query
    useEffect(() => {
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            const newFiltered = serviceSpecializations.filter(specialization => {
                return (
                    (specialization.service_type && specialization.service_type.toLowerCase().includes(lowerCaseQuery)) ||
                    (specialization.specialization && specialization.specialization.toLowerCase().includes(lowerCaseQuery))
                );
            });
            setFilteredServiceSpecializations(newFiltered);
        } else {
            setFilteredServiceSpecializations(serviceSpecializations);
        }
        setPage(0); // Reset page to 0 when search query changes
    }, [searchQuery, serviceSpecializations]);


    // Handlers for pagination changes
    const handleRowsPerPageChange = (event, newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to the first page when rows per page changes
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    // Data for the current page
    const paginatedTableData = useMemo(() => {
        return filteredServiceSpecializations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredServiceSpecializations, page, rowsPerPage]);

    const columns = useMemo(() => [
        {
            Header: "S.No",
            accessor: "serial",
            Cell: ({ row }) => (page * rowsPerPage) + row.index + 1, // Correct serial number calculation
            disableSortBy: true,
            align: "left",
        },
        {
            Header: "Service Type",
            accessor: "service_type",
            sortable: true,
        },
        {
            Header: "Service Specialization",
            accessor: "specialization",
            sortable: true,
            Cell: ({ value }) => (
                <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", textAlign: "center" }}>
                    {value.replace(/(.{45})/g, "$1\n")}
                </div>
            ),
        },
        {
            Header: "Options",
            accessor: "options",
            align: "center",
            Cell: ({ row }) => (
                <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                    <IconButton
                        color="success"
                        onClick={() => {
                            setOpenEditModal(true);
                            const rowData = { ...row.original }; // Create a copy to avoid direct mutation
                            rowData.specialization = rowData.specialization.split(",").map(item => item.trim());
                            setEditInput(rowData);
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={() => {
                            setDeleteServiceSpecializationId(row.original.service_specialization_id);
                            setOpenDeleteModal(true);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </div>
            )
        }
    ], [page, rowsPerPage]); // Add page and rowsPerPage as dependencies for useMemo


    // Transform API data to fit DataTable structure
    const data = useMemo(() =>
        Array.isArray(paginatedTableData) && paginatedTableData.length > 0
            ? paginatedTableData.map(serviceSpecialization => ({
                service_specialization_id: serviceSpecialization.id, // Ensure this matches your API response for ID
                service_type: serviceSpecialization.service_type,
                specialization: serviceSpecialization.service_specializations // Ensure this matches your API response for specializations
            }))
            : [],
        [paginatedTableData]
    );

    console.log(data); // For debugging


    // ----------------------------------------------------------
    // Service Specialization CRUD API Calls
    // ----------------------------------------------------------


    const fetchServiceSpecializations = async () => {
        setLoading(true);
        try {
            const response = await api.get("/service_management/service_specializations/");
            console.log("Fetched service_specializations:", response);
            const fetchedServiceSpecializations = response.data.service_specializations;
            setServiceSpecializations(fetchedServiceSpecializations);
            setFilteredServiceSpecializations(fetchedServiceSpecializations); // Initialize filtered data
        } catch (error) {
            console.error("Error fetching service specializations:", error);
            Swal.fire("Error", "Failed to load service specializations. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    };


    const deleteServiceSpecialization = async (event) => {
        event.preventDefault();
        try {
            handleClose();

            const response = await api.delete(`/service_management/service_specializations/${deleteServiceSpecializationId}`);

            if (response.data.status === 1) {
                Swal.fire({
                    title: 'Service specialization deleted successfully!',
                    icon: 'success',
                    timer: 1500
                });
                fetchServiceSpecializations();
            } else {
                Swal.fire({
                    title: 'Error',
                    text: response.data.message,
                    icon: 'error',
                });
            }
        } catch (error) {
            console.error("Error deleting service specialization:", error);
            Swal.fire({
                title: 'Error',
                text: 'An unexpected error occurred',
                icon: 'error',
            });
        }
    };

    // ----------------------------------------------------------


    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                                <MDTypography variant="h6" color="white">Service Specialization</MDTypography>
                            </MDBox>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={2} pl={3} pr={3}>
                                <TextField
                                    label="Search Type..."
                                    variant="outlined"
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{ width: '300px' }}
                                />
                                <MDButton color="success" onClick={() => setOpenAddModal(true)}>Add Service Specialization</MDButton>
                            </MDBox>
                            <MDBox p={3}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="50svh"
                            >
                                {loading ? <CircularProgress color="primary" /> : (
                                    <MDBox width="100%" display="flex" justifyContent="flex-start">
                                        <DataTable
                                            table={{ columns: columns, rows: data }}
                                            isSorted={true}
                                            entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                                            showTotalEntries={true}
                                            canSearch={false} 
                                            noEndBorder
                                            pagination={{
                                                variant: "contained",
                                                color: "dark",
                                                page: page,
                                                onPageChange: handlePageChange,
                                                rowsPerPage: rowsPerPage,
                                                onRowsPerPageChange: handleRowsPerPageChange,
                                                count: filteredServiceSpecializations.length,
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* ------------------------------------------------- */}
            {/* Add Service Specialization Model Component */}
            {/* ------------------------------------------------- */}

            <ServiceSpecializationAddModel open={openAddModal} handleClose={handleCloseAddModal} />
            {/* ------------------------------------------------- */}
            {/* Edit Service Specialization Model Component */}
            {/* ------------------------------------------------- */}

            <ServiceSpecializationEditModel
                open={openEditModal}
                handleClose={handleClose}
                editInput={editInput}
                handleEditChange={handleEditChange}
            />

            {/* ------------------------------------------------- */}

            {/* Delete Model */}
            <Dialog open={openDeleteModal} onClose={handleClose}>
                <MDBox component="form" onSubmit={deleteServiceSpecialization}>
                    <DialogTitle>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <WarningIcon style={{ color: red[700], fontSize: 40 }} />
                            </Grid>
                            <Grid item>
                                <MDTypography variant="h6" component="span">
                                    Confirm Delete
                                </MDTypography>
                            </Grid>
                        </Grid>
                    </DialogTitle>

                    <DialogContent>
                        <MDBox p={2} style={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
                            <MDTypography variant="body1">
                                Are you sure you want to delete this service specialization ? This action cannot be undone.
                            </MDTypography>
                        </MDBox>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={handleClose} style={{ color: 'gray' }}>Cancel</MDButton>
                        <MDButton type="submit" color="primary">Delete</MDButton>
                    </DialogActions>
                </MDBox>
            </Dialog>

        </DashboardLayout>
    )
}